import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Typography,
  Box,
  Snackbar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Grid,
  TableContainer,
  TableSortLabel,
  TablePagination,
  Tooltip,
} from '@material-ui/core';
import axios from 'axios';
import envConfig from '../../bin/env.config';
import { useSelector } from 'react-redux';
import {
  STATUS_OPTIONS,
  formatOodEditedBy,
} from '../../utility/statusConfig';
import CopyableValue from './CustomerReport/CopyableValue';
import StalePriceReportLayout, { useStalePriceReportStyles } from './CustomerReport/StalePriceReportLayout';
import StalePriceLastCollectedCell from './CustomerReport/StalePriceLastCollectedCell';
import StalePriceReviewStatusCell from './CustomerReport/StalePriceReviewStatusCell';
import StalePriceReportTypeChip from './CustomerReport/StalePriceReportTypeChip';
import {
  REPORT_TYPE_OPTIONS,
  formatDate,
  formatDateTime,
  getSortValue,
  DEFAULT_FILTERS,
  parseStalePriceFiltersFromSearch,
  serializeStalePriceFiltersToSearch,
} from './CustomerReport/stalePriceReportConfig';

const TABLE_COLUMNS = [
  { label: 'Customer Report', sortKey: 'reported_at', type: 'date' },
  { label: 'Product', sortKey: 'product_name', type: 'string' },
  { label: 'Store', sortKey: 'location_name', type: 'string' },
  { label: 'IDs', sortKey: null },
  { label: 'Last Collected', sortKey: 'current_updated_at', type: 'date' },
  { label: 'Review Status', sortKey: 'manual_check_status_id', type: 'number' },
  { label: 'Reports', sortKey: 'user_report_count', type: 'number' },
];

const openExternalLinkAndCopyPostal = (report) => {
  if (!report.external_link) return;
  let urlToOpen = report.external_link;
  try {
    const url = new URL(report.external_link);
    if (report.store_id) {
      url.searchParams.set('bcpc_store_id', report.store_id);
    }
    if (report.company_product_id) {
      url.searchParams.set('bcpc_company_product_id', report.company_product_id);
    }
    urlToOpen = url.toString();
  } catch (error) {
    // Fall back to the original link.
  }
  window.open(urlToOpen, '_blank');
  if (report.store_postal) {
    navigator.clipboard.writeText(String(report.store_postal));
  }
};

export default function CompanyReportToolStalePriceReportsPage(props) {
  const history = useHistory();
  const classes = useStalePriceReportStyles();
  const token = useSelector((state) => state.session.token);
  const user_type_id = props.match.params.user_type_id;
  const project_id = props.match.params.project_id;

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [saveSnackbar, setSaveSnackbar] = useState('');
  const [filters, setFilters] = useState(() =>
    parseStalePriceFiltersFromSearch(props.location.search)
  );
  const [orderBy, setOrderBy] = useState('reported_at');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filtersInitialized, setFiltersInitialized] = useState(false);

  const syncFiltersToUrl = useCallback(
    (nextFilters) => {
      const search = serializeStalePriceFiltersToSearch(nextFilters);
      history.replace({
        pathname: props.location.pathname,
        search,
      });
    },
    [history, props.location.pathname]
  );

  const handleFilterChange = useCallback(
    (nextFilters) => {
      setFilters(nextFilters);
      setPage(0);
      syncFiltersToUrl(nextFilters);
    },
    [syncFiltersToUrl]
  );

  const handleClearAllFilters = useCallback(() => {
    handleFilterChange({ ...DEFAULT_FILTERS });
  }, [handleFilterChange]);

  useEffect(() => {
    if (!filtersInitialized) {
      const parsed = parseStalePriceFiltersFromSearch(props.location.search);
      setFilters(parsed);
      setFiltersInitialized(true);
    }
  }, [props.location.search, filtersInitialized]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${envConfig.API_ROOT}${envConfig.API_VERSION}/srp-pricing-tool/stale-price-reports`,
        {
          user_type_id,
          city: filters.city,
          banners: filters.banners,
          report_type: filters.reportType,
          fromDate: filters.fromDate || undefined,
          toDate: filters.toDate || undefined,
        },
        {
          headers: {
            Authorization: token,
          },
        }
      );
      setReports(response.data.data || []);
    } catch (error) {
      console.error('Error fetching stale price reports:', error);
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user_type_id && filtersInitialized) {
      fetchReports();
    }
  }, [
    filters.city,
    filters.banners,
    filters.reportType,
    filters.fromDate,
    filters.toDate,
    user_type_id,
    filtersInitialized,
  ]);

  useEffect(() => {
    setPage(0);
  }, [filters.search, filters.review, filters.reporter, orderBy, order]);

  const handleStatusSaved = (statusData, reportContext) => {
    if (!reportContext) return;
    setReports((prev) =>
      prev.map((r) =>
        r.sighting_id === reportContext.sighting_id
          ? {
              ...r,
              manual_check_status_id: Number(statusData.status_id),
              manual_check_edited_at:
                statusData.edited_at || new Date().toISOString(),
              manual_check_edited_by:
                statusData.edited_by || r.manual_check_edited_by,
              has_valid_manual_check: true,
            }
          : r
      )
    );
    setSaveSnackbar('Status saved');
  };

  const reporterOptions = useMemo(() => {
    const byId = new Map();
    reports.forEach((report) => {
      const id = report.reported_by;
      if (!id || byId.has(id)) return;
      const name = (report.reporter_name || '').trim();
      const email = (report.reporter_email || '').trim();
      let label = name;
      if (name && email) {
        label = `${name} (${email})`;
      } else if (email) {
        label = email;
      } else {
        label = `User #${id}`;
      }
      byId.set(id, { value: String(id), label });
    });
    return Array.from(byId.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [reports]);

  const filteredReports = useMemo(() => {
    let result = reports;

    if (filters.reporter) {
      result = result.filter((r) => String(r.reported_by) === filters.reporter);
    }

    if (filters.review === 'needs_review') {
      result = result.filter((r) => !r.has_valid_manual_check);
    } else if (filters.review === 'reviewed') {
      result = result.filter((r) => r.has_valid_manual_check);
    }

    if (!filters.search) return result;
    const lowerFilter = filters.search.toLowerCase();
    return result.filter(
      (report) =>
        (report.product_name || '').toLowerCase().includes(lowerFilter) ||
        (report.pmg_name || '').toLowerCase().includes(lowerFilter) ||
        (report.location_name || '').toLowerCase().includes(lowerFilter) ||
        (report.store_address || '').toLowerCase().includes(lowerFilter) ||
        (report.store_postal || '').toLowerCase().includes(lowerFilter) ||
        (report.banner_name || '').toLowerCase().includes(lowerFilter) ||
        (report.city || '').toLowerCase().includes(lowerFilter) ||
        (report.note || '').toLowerCase().includes(lowerFilter) ||
        (report.reporter_email || '').toLowerCase().includes(lowerFilter) ||
        (report.reporter_name || '').toLowerCase().includes(lowerFilter) ||
        String(report.product_matching_group_id).includes(lowerFilter) ||
        String(report.company_product_id).includes(lowerFilter) ||
        String(report.product_id || '').includes(lowerFilter) ||
        String(report.store_id || '').includes(lowerFilter)
    );
  }, [reports, filters.search, filters.review, filters.reporter]);

  const sortedReports = useMemo(() => {
    const column = TABLE_COLUMNS.find((col) => col.sortKey === orderBy);
    const type = column?.type || 'string';
    const comparator = (a, b) => {
      const aVal = getSortValue(a, orderBy, type);
      const bVal = getSortValue(b, orderBy, type);
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    };
    return [...filteredReports].sort(comparator);
  }, [filteredReports, orderBy, order]);

  const paginatedReports = useMemo(() => {
    return sortedReports.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [sortedReports, page, rowsPerPage]);

  const needsReviewCount = useMemo(
    () => filteredReports.filter((r) => !r.has_valid_manual_check).length,
    [filteredReports]
  );
  const reviewedCount = useMemo(
    () => filteredReports.filter((r) => r.has_valid_manual_check).length,
    [filteredReports]
  );

  const handleSort = (sortKey) => {
    const isAsc = orderBy === sortKey && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(sortKey);
  };

  const handleExportCSV = () => {
    const headers = [
      'Report ID',
      'Sighting ID',
      'Reported At',
      'Report Type',
      'Note',
      'Reporter Name',
      'Reporter Email',
      'PMG ID',
      'PMG Name',
      'Product Name',
      'Product ID',
      'Company Product ID',
      'Store ID',
      'Store/Banner',
      'Postal Code',
      'City',
      'Last Collected',
      'Report Count',
      'Manual Check Status',
      'Manual Check At',
      'Manual Check By',
      'Team Reviewed',
    ];

    let csv = headers.join(',') + '\n';
    sortedReports.forEach((report) => {
      const rowData = [
        report.user_report_id,
        report.sighting_id,
        `"${formatDateTime(report.reported_at).replace(/"/g, '""')}"`,
        `"${(REPORT_TYPE_OPTIONS[report.report_type] || report.report_type || '').replace(/"/g, '""')}"`,
        `"${(report.note || '').replace(/"/g, '""')}"`,
        `"${(report.reporter_name || '').replace(/"/g, '""')}"`,
        `"${(report.reporter_email || '').replace(/"/g, '""')}"`,
        report.product_matching_group_id,
        `"${(report.pmg_name || '').replace(/"/g, '""')}"`,
        `"${(report.product_name || '').replace(/"/g, '""')}"`,
        report.product_id || '',
        report.company_product_id,
        report.store_id,
        `"${(report.location_name || '').replace(/"/g, '""')}"`,
        `"${(report.store_postal || '').replace(/"/g, '""')}"`,
        `"${(report.city || '').replace(/"/g, '""')}"`,
        formatDate(report.current_updated_at),
        report.user_report_count,
        report.manual_check_status_id != null
          ? STATUS_OPTIONS[report.manual_check_status_id] || report.manual_check_status_id
          : '',
        `"${formatDateTime(report.manual_check_edited_at).replace(/"/g, '""')}"`,
        `"${formatOodEditedBy(report.manual_check_edited_by, {
          statusId: report.manual_check_status_id,
        }).replace(/"/g, '""')}"`,
        report.has_valid_manual_check ? 'Yes' : 'No',
      ];
      csv += rowData.join(',') + '\n';
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'customer_stale_price_reports.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const showEmptyState = !loading && sortedReports.length === 0;

  return (
    <>
      <StalePriceReportLayout
        view="reports"
        title="Stale Price Reports"
        userTypeId={user_type_id}
        projectId={project_id}
        token={token}
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearAllFilters={handleClearAllFilters}
        reporterOptions={reporterOptions}
        countLabel="Reports"
        totalCount={filteredReports.length}
        needsReviewCount={needsReviewCount}
        reviewedCount={reviewedCount}
        loading={loading}
        onExport={handleExportCSV}
        showEmptyState={showEmptyState}
        emptyMessage="No customer reports found."
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TableContainer className={classes.tableContainer}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {TABLE_COLUMNS.map((col) => (
                      <TableCell
                        key={col.label}
                        style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}
                      >
                        {col.sortKey ? (
                          <TableSortLabel
                            active={orderBy === col.sortKey}
                            direction={orderBy === col.sortKey ? order : 'asc'}
                            onClick={() => handleSort(col.sortKey)}
                          >
                            {col.label}
                          </TableSortLabel>
                        ) : (
                          col.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedReports.map((report) => (
                    <TableRow
                      key={report.user_report_id}
                      className={
                        report.has_valid_manual_check
                          ? classes.tableRowHover
                          : `${classes.tableRowHover} ${classes.needsReviewRow}`
                      }
                    >
                      <TableCell style={{ minWidth: 180 }}>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                          {formatDateTime(report.reported_at)}
                        </Typography>
                        <StalePriceReportTypeChip reportType={report.report_type} />
                        <Tooltip title={report.note || ''} placement="top-start">
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{
                              maxWidth: 220,
                              wordBreak: 'break-word',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                            }}
                          >
                            {report.note || '—'}
                          </Typography>
                        </Tooltip>
                        <Typography variant="caption" display="block" style={{ marginTop: 4 }}>
                          {report.reporter_name || '—'}
                        </Typography>
                        {report.reporter_email && (
                          <Typography variant="caption" display="block" color="textSecondary">
                            {report.reporter_email}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell style={{ minWidth: 160 }}>
                        <span
                          onClick={() => openExternalLinkAndCopyPostal(report)}
                          style={{
                            cursor: report.external_link ? 'pointer' : 'default',
                            fontWeight: 500,
                          }}
                        >
                          {report.product_name}
                        </span>
                        <Typography variant="caption" display="block" color="textSecondary">
                          {report.pmg_name}
                        </Typography>
                      </TableCell>
                      <TableCell style={{ minWidth: 140 }}>
                        <Typography variant="body2">{report.location_name}</Typography>
                        {report.store_address && (
                          <Typography variant="caption" display="block">
                            <i>{report.store_address}</i>
                          </Typography>
                        )}
                        <Typography variant="caption" display="block">
                          {report.city || '—'}
                          {report.banner_name ? ` · ${report.banner_name}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell style={{ minWidth: 160 }}>
                        <CopyableValue label="CP" value={report.company_product_id} />
                        <CopyableValue
                          label="Product"
                          value={report.product_id}
                          href={
                            report.product_id
                              ? `/product/${report.product_id}`
                              : undefined
                          }
                        />
                        <CopyableValue
                          label="PMG"
                          value={report.product_matching_group_id}
                          href={
                            report.product_matching_group_id
                              ? `/matching-group/${report.product_matching_group_id}`
                              : undefined
                          }
                        />
                        <CopyableValue label="Postal" value={report.store_postal} />
                        <CopyableValue label="Store" value={report.store_id} />
                      </TableCell>
                      <StalePriceLastCollectedCell
                        row={report}
                        onStatusSaved={handleStatusSaved}
                      />
                      <TableCell style={{ minWidth: 140 }}>
                        <StalePriceReviewStatusCell row={report} />
                      </TableCell>
                      <TableCell>{report.user_report_count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {sortedReports.length > 0 && (
              <TablePagination
                component="div"
                count={sortedReports.length}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            )}
          </Grid>
        </Grid>
      </StalePriceReportLayout>

      <Snackbar
        open={showError}
        autoHideDuration={3000}
        onClose={() => setShowError(false)}
        message="Failed to load customer reports."
      />
      <Snackbar
        open={Boolean(saveSnackbar)}
        autoHideDuration={2000}
        onClose={() => setSaveSnackbar('')}
        message={saveSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}
